import { Injectable, OnDestroy, inject } from '@angular/core';
import { BehaviorSubject, combineLatest, map, Observable } from 'rxjs';
import { IPolicy } from '../../../policy/policy-and-product/models/policy-and-product.model';
import { IApproval, IApprovalDiagnosis } from '../models/preauth-request.model';
import { ICommonAPIResponse, IDateMinMaxConfig, IDaySupply, IDaySupplyResponse } from '../../../../../../shared/models/common-models';
import { HttpRequestService } from '../../../../../../shared/services/http-request/http-request.service';
import { IPriceListForPreAuth } from '../models/preauth-request.model';
import { AppDatepicker, ITextValueOption, TranslationService } from '@waseel-asp/tpa-waseel-lib-staging';
import { API } from '../../../../../../shared/constants/api-url';
import moment, { DurationInputArg2 } from 'moment';
import { IBeneficiaryDetails } from '../../../beneficiary/models/beneficiary.model';
import { DATE_FORMATS, ENABLE_DAYS } from '../../../../../../shared/constants/system-constants.model';
import { IEncounterClass } from '../../../configuration/encounter-class/models/encounter-class.model';
import { FormGroup, Validators } from '@angular/forms';
import { ToastrHelperService } from '../../../../../../shared/services/common/toast-helper.service';
import { getDatePickerConfig } from '../../../../../../shared/functions/config-function';
import { DateConfigService } from '../../../../../../shared/services/date-config/date-config.service';
import { ReportType } from '../../../reports/models/reports.model';

@Injectable({
  providedIn: 'root'
})
export class FormService implements OnDestroy {
  private httpRequest = inject(HttpRequestService);
  private translation = inject(TranslationService);
  private toastrHelperService = inject(ToastrHelperService);
  private dtConfigService = inject(DateConfigService);

  constructor() {
    combineLatest([
      this.selectedPolicySubject.asObservable(),
      this.dateOrderedSubject.asObservable(),
      this.typeSubject.asObservable(),
      this.careTeamSubject.asObservable(),
      this.providerSubject.asObservable()
    ]).pipe(
      map(([searchBenef, dateOrdered, type, careTeam, provider]) => {
        const isCareTeamValid = this.isAdvancePreAuthRequest.getValue() || careTeam?.some(member => member) || false;
        return !!(searchBenef && (this.dateOrderedInitialized ? dateOrdered : true) && type && isCareTeamValid && provider);
      })
    ).subscribe(isValid => {
      this.AddItembuttonStateSubject.next(isValid);
    });

    combineLatest([
      this.selectedPolicySubject.asObservable(),
      this.dateOrderedSubject.asObservable(),
      this.typeSubject.asObservable(),
      this.providerSubject.asObservable()
    ]).pipe(
      map(([selectedPolicy, dateOrdered, type, provider]) => {
        // If serviceTypeSubject already has a value or no values have changed, skip the API call
        if (
          this.serviceTypeSubject.getValue() !== null && // Check if serviceTypeSubject is not null
          this.previousValues.selectedPolicy === selectedPolicy &&
          this.previousValues.dateOrdered === dateOrdered &&
          this.previousValues.type === type &&
          this.previousValues.provider === provider
        ) {
          // No change, no need for API call
          return null;
        }

        // If any of the values have changed, proceed with the API call
        if (selectedPolicy && dateOrdered && type && provider) {
          const params = {
            "policyId": selectedPolicy.policyId,
            "providerId": provider,
            "orderDate": this.convertDateTimeToAPISepcific(dateOrdered),
            "claimTypeId": type,
            "serviceTypeId": null,
            "searchCriteria": {
              "columns": [
                {
                  "name": "priceListId",
                  "searchable": true,
                  "orderable": true,
                  "filterable": true,
                  "filterSearch": {
                    "value": "",
                    "condition": "IsEqualTo"
                  },
                  "excelColumnName": "priceListId"
                }
              ],
              "defaultSortingColumn": "priceListId",
              "pageNumber": 0,
              "pageSize": 0,
              "sortBy": "priceListId",
              "sortOrder": "ASC",
              "generalSearch": "",
              "isSearchByCriteria": true
            }
          };

          // Make the API call
          this.httpRequest.post(API.PRE_AUTH.PRICELIST_FOR_APPROVAL, params).subscribe({
            next: (res: ICommonAPIResponse<IPriceListForPreAuth>) => {
              if (res && res.results)
                this.serviceTypeSubject.next(res.results);
              else
                this.serviceTypeSubject.next(null);
              this.loadPriceList.next(true);
            },
            error: () => {
              this.loadPriceList.next(false);
              this.IsContractNotFound.next(true);
              this.serviceTypeSubject.next(null);
            }
          });

          // Store the current values for future comparison
          this.previousValues = { selectedPolicy, dateOrdered, type, provider };
        } else
          this.serviceTypeSubject.next(null);
      })
    ).subscribe({
      next: () => {
        // Optional: Add any logic needed when the result is received
      }
    });
  }

  private previousValues = { selectedPolicy: null, dateOrdered: null, type: null, provider: null };
  private typeValueSource = new BehaviorSubject<string | null>(null);
  typeValue$ = this.typeValueSource.asObservable();

  selectedPolicySubject = new BehaviorSubject<IPolicy | null>(null);
  dateOrderedSubject = new BehaviorSubject<Date | null>(new Date());
  typeSubject = new BehaviorSubject<string | null>(null);
  subTypeSubject = new BehaviorSubject<string | null>(null);
  private careTeamSubject = new BehaviorSubject<string[]>([]);
  careTeam$ = this.careTeamSubject.asObservable();
  providerSubject = new BehaviorSubject<string | null>(null);
  private AddItembuttonStateSubject = new BehaviorSubject<boolean>(false);
  private dateOrderedInitialized = false;

  diagnosisTypeSubject = new BehaviorSubject<any[]>([]);
  diagnosisTypeSubject$ = this.diagnosisTypeSubject.asObservable();

  isnewBornSubject = new BehaviorSubject<boolean>(false);
  isnewBorn$ = this.isnewBornSubject.asObservable();

  conditionOnSetSubject = new BehaviorSubject<any[]>([]);
  conditionOnSetSubject$ = this.conditionOnSetSubject.asObservable();

  admissionTypeSubject = new BehaviorSubject<any[]>([]);
  admissionTypeSubject$ = this.admissionTypeSubject.asObservable();

  serviceTypeSubject = new BehaviorSubject<IPriceListForPreAuth[]>([]);
  serviceTypeSubject$ = this.serviceTypeSubject.asObservable();

  selectedBeneficiarySubject = new BehaviorSubject<IBeneficiaryDetails>(null);
  selectedBeneficiarySubject$ = this.selectedBeneficiarySubject.asObservable();

  quantityCodeSubject = new BehaviorSubject<any[]>([]);
  quantityCodeSubject$ = this.quantityCodeSubject.asObservable();

  claimStatusSubject = new BehaviorSubject<ITextValueOption[]>([]);
  claimStatusSubject$ = this.claimStatusSubject.asObservable();

  absenceOfScientificCodeSubject = new BehaviorSubject<ITextValueOption[]>([]);
  absenceOfScientificCodeSubject$ = this.absenceOfScientificCodeSubject.asObservable();

  approvalDataSubject = new BehaviorSubject<IApproval>(null);
  approvalDataSubject$ = this.approvalDataSubject.asObservable();

  isEditModeSubject = new BehaviorSubject<boolean>(false);
  isEditModeSubject$ = this.isEditModeSubject.asObservable();

  IsHospitalizationShow = new BehaviorSubject<boolean>(false);
  IsHospitalizationShow$ = this.IsHospitalizationShow.asObservable();

  IsContractNotFound = new BehaviorSubject<boolean>(false);
  IsContractNotFound$ = this.IsContractNotFound.asObservable();

  IsClassChanged = new BehaviorSubject<boolean>(false);
  IsClassChanged$ = this.IsClassChanged.asObservable();

  isEmergencyShow = new BehaviorSubject<boolean>(false);
  isEmergencyShow$ = this.isEmergencyShow.asObservable();

  isEncounterShow = new BehaviorSubject<boolean>(false);
  isEncounterShow$ = this.isEncounterShow.asObservable();

  isVisionShow = new BehaviorSubject<boolean>(false);
  isVisionShow$ = this.isVisionShow.asObservable();

  isServiceEventTypeShow = new BehaviorSubject<boolean>(false);
  isServiceEventTypeShow$ = this.isServiceEventTypeShow.asObservable();

  encounterClass = new BehaviorSubject<IEncounterClass[]>([]);
  encounterClass$ = this.encounterClass.asObservable();
  filterEncounterType = new BehaviorSubject<IEncounterClass[]>([]);

  isAttachmentUpdate = new BehaviorSubject<boolean>(true);
  isAttachmentUpdate$ = this.isAttachmentUpdate.asObservable();

  private processSubmitSubject = new BehaviorSubject<boolean>(false);
  processSubmitSubject$ = this.processSubmitSubject.asObservable();

  encounterStartDate = new BehaviorSubject<Date>(null);
  encounterStartDate$ = this.encounterStartDate.asObservable();

  encounterEndDate = new BehaviorSubject<Date>(null);
  encounterEndDate$ = this.encounterEndDate.asObservable();

  isFollowUpSubject = new BehaviorSubject<boolean>(false);
  isFollowUpSubject$ = this.isFollowUpSubject.asObservable();

  isAdvancePreAuthRequest = new BehaviorSubject<boolean>(false);
  isAdvancePreAuthRequest$ = this.isAdvancePreAuthRequest.asObservable();

  isSupportingInfoAttachmentUpdate = new BehaviorSubject<boolean>(false);
  isSupportingInfoAttachmentUpdate$ = this.isSupportingInfoAttachmentUpdate.asObservable();

  allSupportingInfoAttachmentIds = new BehaviorSubject<string[]>([]);
  allSupportingInfoAttachmentIds$ = this.allSupportingInfoAttachmentIds.asObservable();

  maxAttachmentSize = new BehaviorSubject<number>(0);
  maxAttachmentSize$ = this.maxAttachmentSize.asObservable();

  maxAttachmentSeqNo = new BehaviorSubject<number>(0);
  maxAttachmentSeqNo$ = this.maxAttachmentSeqNo.asObservable();

  isdublicateAttachment = new BehaviorSubject<number>(null);
  isdublicateAttachment$ = this.isdublicateAttachment.asObservable();

  supportingInfoAttachmentNames = new BehaviorSubject<string[]>([]);
  supportingInfoAttachmentNames$ = this.supportingInfoAttachmentNames.asObservable();

  commonAttachmentNames = new BehaviorSubject<string[]>([]);
  commonAttachmentNames$ = this.commonAttachmentNames.asObservable();

  loadPriceList = new BehaviorSubject<boolean>(false);

  addDaySupplyControl = new BehaviorSubject<IDaySupply>(null);
  addDaySupplyControl$ = this.addDaySupplyControl.asObservable();

  updateSupportiveInfoSelected = new BehaviorSubject<IDaySupplyResponse>(null);
  updateSupportiveInfoSelected$ = this.updateSupportiveInfoSelected.asObservable();

  usedSupportiveInfo = new BehaviorSubject<number[]>(null);
  usedSupportiveInfo$ = this.usedSupportiveInfo.asObservable();

  fromClaimReimbursement = new BehaviorSubject<boolean>(false);
  fromClaimReimbursement$ = this.fromClaimReimbursement.asObservable();

  claimReimbursementId = new BehaviorSubject<string>(null);
  claimReimbursementId$ = this.claimReimbursementId.asObservable();

  accidentDate = new BehaviorSubject<string>(null);

  createdDate = new BehaviorSubject<Date>(null);

  private fieldLabels: { [key: string]: string } = {
    beneficiaryId: 'BASIC_INFO',
    dateOrdered: 'BASIC_INFO',
    typeId: 'BASIC_INFO',
    subTypeId: 'BASIC_INFO',
    providerId: 'BASIC_INFO',
    approvalEncounterInfo: 'BASIC_INFO',
    approvalEmergencyInfo: 'BASIC_INFO',
    approvalHospitalizationInfo: 'BASIC_INFO',
    referralApprovalId: 'BASIC_INFO',
    approvalAccidentDetail: 'BASIC_INFO',
    approvalCareTeams: 'CARE_TEAM',
    approvalDiagnosis: 'DIAGNOSIS',
    approvalSupportingInfos: 'SUPPORTING_INFO',
    approvalSupportingInfoLabTests: 'SUPPORTING_INFO',
    approvalSupportingInfoDiagnosis: 'SUPPORTING_INFO',
    care: 'CARE_TEAM',
    approvalItems: 'ITEMS',
    visionPrescDateWritten: 'BASIC_INFO',
    communicationFormGroup: 'COMMUNICATION'
  };

  public fieldMapper = {
    'BASIC_INFO': 0,
    'CARE_TEAM': 1,
    'DIAGNOSIS': 2,
    'SUPPORTING_INFO': 3,
    'ITEMS': 5,
    'COMMUNICATION': 6
  };

  private ClaimFieldLabels: { [key: string]: string } = {
    dateOrdered: 'CLAIM_TITLE',
    typeId: 'CLAIM_TITLE',
    subTypeId: 'CLAIM_TITLE',
    episodeNo: "CLAIM_TITLE",
    providerId: 'CLAIM_TITLE',
    referralApprovalId: 'CLAIM_TITLE',
    beneficiaryId: 'BENEFICIARY',
    policyId: 'BENEFICIARY',
    approvalCareTeams: 'CARE_TEAM',
    approvalDiagnosis: 'DIAGNOSIS',
    approvalSupportingInfos: 'SUPPORTING_INFO',
    approvalSupportingInfoLabTests: 'SUPPORTING_INFO',
    approvalSupportingInfoDiagnosis: 'SUPPORTING_INFO',
    care: 'CARE_TEAM',
    approvalItems: 'ITEMS',
    approvalAccidentDetail: 'ACCIDENT',
    approvalEncounterInfo: 'ENCOUNTER',
    approvalEmergencyInfo: 'ENCOUNTER',
    approvalHospitalizationInfo: 'ENCOUNTER',
    visionPrescDateWritten: 'VISION_PRESCRIPTION',
  };

  public ClaimFieldMapper = {
    'CLAIM_TITLE': 0,
    'BENEFICIARY': 1,
    'CARE_TEAM': 2,
    'DIAGNOSIS': 3,
    'SUPPORTING_INFO': 4,
    'ITEMS': 6,
    'ACCIDENT': 7,
    'ENCOUNTER': 8,
    'VISION_PRESCRIPTION': 8
  };

  private supoortingItemListSubject = new BehaviorSubject<any[]>([]);
  supportingItemList$ = this.supoortingItemListSubject.asObservable();
  private diagnosisListSubject = new BehaviorSubject<IApprovalDiagnosis[]>([]);
  diagnosisList$ = this.diagnosisListSubject.asObservable();

  private doseTypeConfigSubject = new BehaviorSubject<ITextValueOption[] | null>(null);
  private rateTypeConfigSubject = new BehaviorSubject<ITextValueOption[] | null>(null);

  doseTypeConfig$ = this.doseTypeConfigSubject.asObservable();
  rateTypeConfig$ = this.rateTypeConfigSubject.asObservable();

  private reportTypeSubject = new BehaviorSubject<ReportType | null>(null);
  reportType$ = this.reportTypeSubject.asObservable();

  private reportDynamicChartIdSubject = new BehaviorSubject<string | null>(null);
  reportDynamicChartId$ = this.reportDynamicChartIdSubject.asObservable();

  private collapsedSubject = new BehaviorSubject<boolean>(true);
  collapsed$ = this.collapsedSubject.asObservable();

  setCollapsed(value: boolean) {
    this.collapsedSubject.next(value);
  }

  getCollapsed() {
    return this.collapsedSubject.value;
  }

  setCurrentReportDynamicChartId(chartId: string | null): void {
    this.reportDynamicChartIdSubject.next(chartId);
  }

  setCurrentReportType(type: ReportType | null): void {
    this.reportTypeSubject.next(type);
  }

  setDoseTypeConfig(data: ITextValueOption[]) {
    this.doseTypeConfigSubject.next(data);
  }

  setRateTypeConfig(data: ITextValueOption[]) {
    this.rateTypeConfigSubject.next(data);
  }

  updateTypeValue(value: string) {
    this.typeValueSource.next(value);
  }

  setEncounterData(encounterData: IEncounterClass[]) {
    this.encounterClass.next(encounterData);
    this.filterEncounterType.next(encounterData);
  }

  setFilterEncounterData(subTypeId: string) {
    const encounterData = subTypeId ? this.encounterClass?.value?.filter(encounter => encounter.subtypeId === subTypeId) : this.encounterClass.value;
    return this.filterEncounterType.next(encounterData);
  }

  getSelectedPolicy(): Observable<IPolicy | null> {
    return this.selectedPolicySubject.asObservable();
  }

  getDateOrdered(): Observable<Date | null> {
    return this.dateOrderedSubject.asObservable();
  }

  getType(): Observable<string | null> {
    return this.typeSubject.asObservable();
  }

  getSubType(): Observable<string | null> {
    return this.subTypeSubject.asObservable();
  }

  updateAttachmentStatus(value: boolean) {
    this.isAttachmentUpdate.next(value);
  }

  updateSupportingInfoAttachmentStatus(value: boolean) {
    this.isSupportingInfoAttachmentUpdate.next(value);
  }

  setSelectedPolicy(value: IPolicy | null): void {
    this.selectedPolicySubject.next(value);
  }

  setselectedBeneficiary(value: IBeneficiaryDetails | null): void {
    this.selectedBeneficiarySubject.next(value);
  }

  setDateOrdered(value: Date | null): void {
    this.dateOrderedInitialized = true;
    this.dateOrderedSubject.next(value);
  }

  setType(value: string | null): void {
    this.typeSubject.next(value);
  }

  setSubType(value: string | null): void {
    this.subTypeSubject.next(value);
  }

  setCareTeam(members: string[]): void {
    this.careTeamSubject.next(members);
  }

  getButtonState(): Observable<boolean> {
    return this.AddItembuttonStateSubject.asObservable();
  }

  get getFieldLabels(): { [key: string]: string } {
    return this.fieldLabels;
  }

  get getClaimFieldLabels(): { [key: string]: string } {
    return this.ClaimFieldLabels;
  }

  updateItemList<T>(newItems: T[]) {
    this.supoortingItemListSubject.next(newItems);
  }

  updateDiagnosisList(items: IApprovalDiagnosis[]) {
    this.diagnosisListSubject.next(items);
  }

  setProvider(provider: string) {
    this.providerSubject.next(provider);
  }

  setEncounterStartDate(date: Date) {
    this.encounterStartDate.next(date);
  }

  setEncounterEndDate(date: Date) {
    this.encounterEndDate.next(date);
  }

  submitProcess(flag: boolean) {
    this.processSubmitSubject.next(flag);
  }

  convertDateTimeToAPISepcific(date: Date | string | null): string | null {
    if (!date) return null;
    return moment(date).utc().format(DATE_FORMATS.API_LONG_DATE_WITH_TIME);
  }

  isDublicateAttachmentSupportingInfo(value: number) {
    this.isdublicateAttachment.next(value);
  }

  updatesupportingInfoAttachmentNames(array: string[]) {
    this.supportingInfoAttachmentNames.next(array);
  }

  updatecommonAttachmentNames(array: string[]) {
    this.commonAttachmentNames.next(array);
  }

  clearHospitalizationInfoFormValidation(formGroup: FormGroup) {
    this.IsHospitalizationShow.next(false);
    ['admitSourceId', 'admissionSpecialityId', 'intendedLengthofStayId', 'dischargeSpecialityId', 'dischargeDispositionId'].forEach((controlName) => {
      const control = formGroup.get(controlName);
      if (control) {
        control.reset();
        control.clearValidators();
        control.setErrors(null);
        control.markAsPristine();
        control.markAsUntouched();
      }
    });
    formGroup.reset();
    formGroup.updateValueAndValidity();
  }

  setHospitalizationInfoFormValidation(formGroup: FormGroup) {
    this.IsHospitalizationShow.next(true);
    formGroup.get('admitSourceId').setValidators(Validators.required);
    formGroup.get('admissionSpecialityId').setValidators(Validators.required);
    formGroup.get('intendedLengthofStayId').setValidators(Validators.required);
    formGroup.get('dischargeSpecialityId').setValidators(Validators.required);
    formGroup.updateValueAndValidity();
  }

  setEmergencyValidation(formGroup: FormGroup) {
    this.isEmergencyShow.next(true);
    formGroup.get('erArrivalCode').setValidators(Validators.required);
    formGroup.get('erServiceStartDate').setValidators(Validators.required);
    formGroup.get('triageCategoryId').setValidators(Validators.required);
    formGroup.get('triageDate').setValidators(Validators.required);
    formGroup.updateValueAndValidity();
  }

  clearEmergencyFormValidation(formGroup: FormGroup) {
    this.isEmergencyShow.next(false);
    ['erArrivalCode', 'erServiceStartDate', 'triageCategoryId', 'triageDate'].forEach((controlName) => {
      const control = formGroup.get(controlName);
      if (control) {
        control.reset();
        control.clearValidators();
        control.setErrors(null);
        control.markAsPristine();
        control.markAsUntouched();
      }
    });
    formGroup.reset();
    formGroup.updateValueAndValidity();
  }

  setEncounterInfoFormValidation(formGroup: FormGroup) {
    this.isEncounterShow.next(true);
    formGroup.get('statusId').setValidators(Validators.required);
    formGroup.get('classId').setValidators(Validators.required);
    formGroup.get('startDate').setValidators(Validators.required);
    formGroup.get('statusId').updateValueAndValidity();
    formGroup.updateValueAndValidity();
  }

  clearEncounterInfoFormValidation(formGroup: FormGroup) {
    this.isEncounterShow.next(false);
    ['statusId', 'classId', 'startDate'].forEach((controlName) => {
      const control = formGroup.get(controlName);
      if (control) {
        control.reset();
        control.clearValidators();
        control.setErrors(null);
        control.markAsPristine();
        control.markAsUntouched();
      }
    });
    formGroup.reset();
    formGroup.updateValueAndValidity();
  }

  ngOnDestroy(): void {
    this.typeSubject.unsubscribe();
    this.typeValueSource.unsubscribe();
    this.fromClaimReimbursement.unsubscribe();
    this.selectedPolicySubject.unsubscribe();
    this.careTeamSubject.unsubscribe();
    this.providerSubject.unsubscribe();
    this.AddItembuttonStateSubject.unsubscribe();
    this.admissionTypeSubject.unsubscribe();
    this.serviceTypeSubject.unsubscribe();
    this.quantityCodeSubject.unsubscribe();
    this.claimStatusSubject.unsubscribe();
    this.approvalDataSubject.unsubscribe();
    this.supoortingItemListSubject.unsubscribe();
    this.diagnosisListSubject.unsubscribe();
    this.isEditModeSubject.unsubscribe();
    this.isnewBornSubject.unsubscribe();
    this.selectedBeneficiarySubject.unsubscribe();
    this.subTypeSubject.unsubscribe();
    this.isEncounterShow.unsubscribe();
    this.IsHospitalizationShow.unsubscribe();
    this.isEmergencyShow.unsubscribe();
    this.isServiceEventTypeShow.unsubscribe();
    this.filterEncounterType.unsubscribe();
    this.IsContractNotFound.unsubscribe();
    this.conditionOnSetSubject.unsubscribe();
    this.isSupportingInfoAttachmentUpdate.unsubscribe();
    this.allSupportingInfoAttachmentIds.unsubscribe();
    this.maxAttachmentSize.unsubscribe();
    this.maxAttachmentSeqNo.unsubscribe();
    this.absenceOfScientificCodeSubject.unsubscribe();
    this.doseTypeConfigSubject.unsubscribe();
    this.rateTypeConfigSubject.unsubscribe();
    this.claimReimbursementId.unsubscribe();
  }

  validatePolicyStartEndDate(policy: IPolicy, orderDate: Date, displayError: boolean): boolean {
    let isValidStartDate = false;
    let isValidEndDate = false;
    const orderedDateObj = moment(orderDate);

    if (policy?.startDate) {
      const policyStartDateObj = moment(policy.startDate);
      if (orderedDateObj.isBefore(policyStartDateObj)) {
        if (displayError)
          this.toastrHelperService.showErrorMessage("PREAUTH.PREAUTH_REQUEST.BASIC_INFO.POLICY_NOT_STARTED");
        return false;
      } else
        isValidStartDate = true;
    }

    if (policy?.endDate) {
      const policyEndDateObj = moment(policy.endDate);
      if (policyEndDateObj.isBefore(orderedDateObj)) {
        if (displayError)
          this.toastrHelperService.showErrorMessage("PREAUTH.PREAUTH_REQUEST.BASIC_INFO.POLICY_EXPIRY");
        return false;
      } else
        isValidEndDate = true;
    }
    return isValidStartDate && isValidEndDate;
  }

  // getUserSupportiveInfo(){
  //   this.approvalDataSubject.pipe(takeUntil('')).subscribe((res) => {
  //     if(res){
  //       const supportiveItems = res.approvalItems.flatMap(item => item.approvalItemSupportingInfos || {});
  //       const seqList = supportiveItems.map(item  => (item as any).seqNo)
  //       console.log(seqList)
  //       this.usedSupportiveInfo.next(seqList)
  //     }
  //   })
  // }

  // updateUsedSupportiveInfo(seqNo:number){
  //   this.usedSupportiveInfo$.subscribe((res : any[]) =>{
  //     if(res){
  //       const updateSeqNo = [...res,seqNo];
  //       this.usedSupportiveInfo.next(updateSeqNo)
  //     }
  //   })
  // }

  passDuplicateDaysOfSupply(payload: IApproval): boolean {
    if (!payload) return false;

    const res = this.supoortingItemListSubject.getValue();
    if (!res) return false;

    const daysupplys = res.filter(item => item.itemName === 'DAYS_SUPPLY').map(item => item.itemNumber);
    for (const element of payload.approvalItems) {
      const duplicateDays = element.approvalItemSupportingInfos;
      const matches = duplicateDays.filter(val => daysupplys.includes(val));
      if (matches.length > 1) {
        this.toastrHelperService.showErrorMessage(this.translation.instant("PREAUTH.PREAUTH_REQUEST.ITEMS.DUPLICATE_DAYS_SUPPLY", { seqNo: element.seqNo }));
        return false;
      }
    }
    return true;
  }

  getFromDateControl(dateConfig: IDateMinMaxConfig): AppDatepicker {
    return getDatePickerConfig(dateConfig.fromControlName, dateConfig.fromLabel, () => {
      const minStart = dateConfig.minYear.unit === 'today' ? ENABLE_DAYS.CURRENT_DATE : this.dtConfigService.getCalculatedDate(dateConfig.minYear.value, 'past', null, null, dateConfig.minYear.unit);
      const maxEnd = dateConfig.maxYear.unit === 'today' ? ENABLE_DAYS.CURRENT_DATE : this.dtConfigService.getCalculatedDate(dateConfig.maxYear.value, 'future', null, null, dateConfig.maxYear.unit);
      const validTo = dateConfig.formGroup()?.get(dateConfig.toControlname)?.value ? moment(dateConfig.formGroup()?.get(dateConfig.toControlname)?.value).toDate() : null;
      const newDate = validTo && validTo >= minStart && validTo <= maxEnd ? validTo : minStart;
      return validTo ? this.dtConfigService.getCalculatedDate(dateConfig.rangeYear.value, 'past', newDate, minStart, dateConfig.rangeYear.unit as DurationInputArg2) : minStart;
    },
      () => {
        return dateConfig.formGroup()?.get(dateConfig.toControlname)?.value ? dateConfig.formGroup()?.get(dateConfig.toControlname)?.value : dateConfig.maxYear.unit === 'today' ? ENABLE_DAYS.CURRENT_DATE : this.dtConfigService.getCalculatedDate(dateConfig.maxYear.value, 'future', null, null, dateConfig.maxYear.unit);
      }, null, 'outline', null, null, null, null, dateConfig?.defaultMinValue);
  }

  getToDateControl(dateConfig: IDateMinMaxConfig): AppDatepicker {
    return getDatePickerConfig(dateConfig.toControlname, dateConfig.toLabel, () => {
      const minStart = dateConfig.minYear.unit === "today" ? ENABLE_DAYS.CURRENT_DATE : this.dtConfigService.getCalculatedDate(dateConfig.minYear.value, 'past', null, null, dateConfig.minYear.unit);
      return dateConfig.formGroup()?.get(dateConfig.fromControlName)?.value ?
        dateConfig.formGroup()?.get(dateConfig.fromControlName)?.value : minStart;
    },
      () => {
        const minStart = dateConfig.minYear.unit === 'today' ? ENABLE_DAYS.CURRENT_DATE : this.dtConfigService.getCalculatedDate(dateConfig.minYear.value, 'past', null, null, dateConfig.minYear.unit);
        const maxEnd = dateConfig.maxYear.unit === 'today' ? ENABLE_DAYS.CURRENT_DATE : this.dtConfigService.getCalculatedDate(dateConfig.maxYear.value, 'future', null, null, dateConfig.maxYear.unit);
        const validFrom = dateConfig.formGroup()?.get(dateConfig.fromControlName)?.value ? moment(dateConfig.formGroup()?.get(dateConfig.fromControlName)?.value).toDate() : null;
        const newDate = validFrom && validFrom >= minStart && validFrom <= maxEnd ? validFrom : maxEnd;
        const finalDate = validFrom ? this.dtConfigService.getCalculatedDate(dateConfig.rangeYear.value, 'future', newDate, maxEnd, dateConfig.rangeYear.unit as DurationInputArg2) : maxEnd;
        return finalDate;
      }, null, 'outline', null, null, null, null, dateConfig?.defaultMaxValue);
  }
}
